import AvatarSolid from '@eduzz/houston-icons/AvatarSolid';
import CancelIcon from '@eduzz/houston-icons/Cancel';
import Typography from '@eduzz/houston-ui/Typography';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import Slide from '@material-ui/core/Slide';
import useTheme from '@material-ui/core/styles/useTheme';
import type { TransitionProps } from '@material-ui/core/transitions';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Spin from 'components/Spin';
import React, { useMemo, useState } from 'react';
import { useObservable } from 'react-use-observable';
import { affiliateCommissionsService } from 'v2/services/reports/affiliateCommissions';

import { Autocomplete } from './Autocomplete';
import { CardsMobile } from './CardsMobile';
import { useStyles } from './styles';
import { Table } from './Table';

import type { IAutocompleteOption } from './Autocomplete';

interface IProps {
  isOpen: boolean;
  handleClose: () => void;
}

const SlideUpTransition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction='up' ref={ref} {...props} />;
});

export const DetailsModal = ({ isOpen, handleClose }: IProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const [selectedOptions, setSelectedOptions] = useState<IAutocompleteOption[]>([]);

  const [details] = useObservable(() => affiliateCommissionsService.getAffiliateCommissionsDetails(), []);
  const [loadingPartnerCommissionsDetails] = useObservable(() => affiliateCommissionsService.getLoadingAffiliateCommissionsDetails(), []);

  const handleTagRemoveClick = (option: IAutocompleteOption) => {
    const filteredOptions = selectedOptions.filter((o) => o.value !== option.value);
    setSelectedOptions(filteredOptions);
  };

  const handleAutoCompleteOnChange = (event: React.ChangeEvent<{}>, value: IAutocompleteOption[]) => {
    setSelectedOptions(value);
  };

  const autocompleteOptions: IAutocompleteOption[] = useMemo(() => {
    return details?.commissions?.map((commission) => {
      const { product } = commission;

      return {
        value: product.id,
        label: `${product.id} - ${product.title}`,
      };
    });
  }, [details]);

  const filteredCommissions = useMemo(() => {
    const selectedOptionsProductIds = selectedOptions.map((option) => option.value);

    if (selectedOptionsProductIds.length) {
      return details?.commissions?.filter((commission) => {
        const { product } = commission;

        return selectedOptionsProductIds.includes(product.id);
      });
    }

    return details?.commissions;
  }, [details, selectedOptions]);

  const handleDialogClose = () => {
    handleClose();
    setSelectedOptions([]);
  };

  return (
    <Dialog classes={{ paper: classes.dialogPaper }} open={isOpen} onClose={handleDialogClose} TransitionComponent={SlideUpTransition} fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Typography weight='semibold'>Mais detalhes</Typography>
        <IconButton className={classes.dialogTitleCloseBtn} onClick={handleDialogClose} aria-label='Fechar modal' size='small'>
          <CancelIcon className={classes.dialogTitleCloseIcon} />
        </IconButton>
      </DialogTitle>
      {loadingPartnerCommissionsDetails && (
        <div className={classes.loadingContainer}>
          <Spin />
        </div>
      )}
      {!loadingPartnerCommissionsDetails && (
        <DialogContent className={classes.dialogContent}>
          <header className={classes.dialogContentHeader}>
            <AvatarSolid className={classes.avatarIcon} />
            <Typography weight='semibold'>
              Afiliado: <span className={classes.affiliateName}>{details?.commissioned_name}</span>
            </Typography>
          </header>

          {details?.commissions?.length > 3 && (
            <Autocomplete
              options={autocompleteOptions}
              selectedOptions={selectedOptions}
              onTagRemoveClick={handleTagRemoveClick}
              onChange={handleAutoCompleteOnChange}
            />
          )}

          {isMobile && <CardsMobile commissions={filteredCommissions} />}
          {!isMobile && <Table commissions={filteredCommissions} />}
        </DialogContent>
      )}
    </Dialog>
  );
};
